import { useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AlertCircle } from 'lucide-react'

export interface PermissionModule {
  key: string
  label: string
  hint?: string
}

export interface ManagerAccessUser {
  id: number
  name?: string
  panel_access?: boolean
  full_access?: boolean
  permissions?: string[]
  commission_enabled?: boolean
  commission_percentage?: number
}

interface Props {
  user: ManagerAccessUser
  /** Módulos agrupados, vindos do servidor — uma lista só, sem cópia no front. */
  catalog: Record<string, PermissionModule[]>
}

/**
 * Acesso e comissão de um gerente.
 *
 * Fica fora do formulário de dados pessoais de propósito: são decisões de natureza
 * diferente (quem pode o quê, quanto ganha) e salvam num endpoint próprio, que nunca
 * aceita nome, e-mail ou papel junto. Misturar tudo num permit só foi exatamente o
 * buraco que o `:role` abriu.
 */
export function ManagerAccessCard({ user, catalog }: Props) {
  const [panelAccess, setPanelAccess] = useState(user.panel_access ?? false)
  const [fullAccess, setFullAccess] = useState(user.full_access ?? false)
  const [permissions, setPermissions] = useState<string[]>(
    user.permissions ?? [],
  )
  const [commissionEnabled, setCommissionEnabled] = useState(
    user.commission_enabled ?? false,
  )
  const [percentage, setPercentage] = useState(
    String(user.commission_percentage ?? 0),
  )
  const [saving, setSaving] = useState(false)

  const toggleModule = (key: string) =>
    setPermissions((current) =>
      current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key],
    )

  const save = () => {
    setSaving(true)
    router.patch(
      `/users/${user.id}/management`,
      {
        management: {
          panel_access: panelAccess,
          full_access: fullAccess,
          permissions,
          commission_enabled: commissionEnabled,
          commission_percentage: percentage,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => toast.success('Acesso e comissão atualizados.'),
        onError: (errors) =>
          toast.error(
            Object.values(errors).flat().join(', ') ||
              'Não foi possível salvar.',
          ),
        onFinish: () => setSaving(false),
      },
    )
  }

  const grantedCount = fullAccess
    ? Object.values(catalog).flat().length
    : permissions.length

  return (
    <Card>
      <CardHeader>
        <CardTitle>Acesso e comissão</CardTitle>
        <CardDescription>
          O que este gerente enxerga no painel e quanto ele ganha nas vendas que
          registrar.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="permissoes">
          <TabsList>
            <TabsTrigger value="permissoes">
              Permissões
              {panelAccess && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                  {grantedCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="comissao">Comissão</TabsTrigger>
          </TabsList>

          <TabsContent value="permissoes" className="space-y-5 pt-4">
            {/* O acesso ao painel é um interruptor separado da lista: um gerente pode
                ter módulos marcados e ainda assim não entrar — é o estado de quem foi
                configurado mas ainda não foi liberado. */}
            <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
              <div>
                <Label htmlFor="panel_access" className="text-sm font-medium">
                  Entrar no painel
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sem isto, ele continua usando só o aplicativo.
                </p>
              </div>
              <Switch
                id="panel_access"
                checked={panelAccess}
                onCheckedChange={setPanelAccess}
              />
            </div>

            <div
              className={panelAccess ? '' : 'opacity-50 pointer-events-none'}
            >
              <div className="flex items-start justify-between gap-4 rounded-lg border p-4 mb-4">
                <div>
                  <Label htmlFor="full_access" className="text-sm font-medium">
                    Acesso a tudo
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Inclusive módulos criados no futuro. Financeiro,
                    configurações e equipe continuam só com você.
                  </p>
                </div>
                <Switch
                  id="full_access"
                  checked={fullAccess}
                  onCheckedChange={setFullAccess}
                />
              </div>

              <div
                className={fullAccess ? 'opacity-50 pointer-events-none' : ''}
              >
                <div className="space-y-5">
                  {Object.entries(catalog).map(([group, modules]) => (
                    <div key={group} className="space-y-2">
                      <h4 className="text-sm font-semibold">{group}</h4>
                      <div className="space-y-2">
                        {modules.map((mod) => (
                          <label
                            key={mod.key}
                            className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:border-foreground/30"
                          >
                            <Checkbox
                              checked={
                                fullAccess || permissions.includes(mod.key)
                              }
                              onCheckedChange={() => toggleModule(mod.key)}
                            />
                            <span>
                              <span className="text-sm font-medium">
                                {mod.label}
                              </span>
                              {mod.hint && (
                                <span className="block text-xs text-muted-foreground">
                                  {mod.hint}
                                </span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="comissao" className="space-y-4 pt-4">
            <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
              <div>
                <Label
                  htmlFor="commission_enabled"
                  className="text-sm font-medium"
                >
                  Pagar comissão
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sobre vendas de produto e de pacote que ele registrar.
                </p>
              </div>
              <Switch
                id="commission_enabled"
                checked={commissionEnabled}
                onCheckedChange={setCommissionEnabled}
              />
            </div>

            {commissionEnabled && (
              <div className="space-y-4">
                <div className="space-y-2 max-w-[200px]">
                  <Label htmlFor="commission_percentage">Porcentagem</Label>
                  <div className="relative">
                    <Input
                      id="commission_percentage"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={percentage}
                      onChange={(e) => setPercentage(e.target.value)}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      %
                    </span>
                  </div>
                </div>

                {/* Estas quatro regras são as que geram dúvida depois. Ficam na tela
                    porque descobrir cada uma por conta própria custa um chamado. */}
                <div className="rounded-lg border bg-muted/40 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <AlertCircle className="h-4 w-4" />
                    Como a comissão é calculada
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
                    <li>
                      A porcentagem é{' '}
                      <strong>congelada no momento da venda</strong>. Mudar este
                      número não altera nada do que já foi vendido.
                    </li>
                    <li>
                      A comissão só conta quando{' '}
                      <strong>você confirma o pagamento</strong>. Estorno ou
                      cancelamento desfaz.
                    </li>
                    <li>
                      Incide sobre o <strong>valor efetivamente pago</strong> —
                      desconto dado por ele reduz a própria comissão.
                    </li>
                    <li>
                      Só vale para vendas{' '}
                      <strong>registradas por ele no painel</strong>. Compra
                      pelo app do aluno ou pelo link público não gera comissão.
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex justify-end pt-6">
          <Button type="button" onClick={save} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar acesso e comissão'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
